import { z } from 'zod';
import { contentIdSchema, dateOnlySchema, isoDateSchema, isoWeekSchema } from './common.js';
import {
  bookmarkKindSchema,
  lessonStatusSchema,
  projectStatusSchema,
  reviewKindSchema,
  reviewRatingSchema,
} from './auth.js';

// ─── Progreso ───────────────────────────────────────────────────────────────

export const updateLessonProgressSchema = z.object({
  status: lessonStatusSchema,
});

export const lessonProgressSchema = z.object({
  lessonId: contentIdSchema,
  status: lessonStatusSchema,
  firstStartedAt: isoDateSchema.nullable().optional(),
  completedAt: isoDateSchema.nullable().optional(),
  lastSeenAt: isoDateSchema.optional(),
  minutesSpent: z.number().int().optional().default(0),
});

// ─── Intentos de ejercicios ─────────────────────────────────────────────────

export const submitExerciseAttemptSchema = z.object({
  passed: z.boolean(),
  testsTotal: z.number().int().min(0),
  testsFailed: z.number().int().min(0),
  hintsUsed: z.number().int().min(0).default(0),
  durationSeconds: z.number().int().min(0).max(86_400).nullable().optional(),
  code: z.string().max(50_000),
});

// ─── Intentos de quizzes ────────────────────────────────────────────────────

export const submitQuizAttemptSchema = z.object({
  score: z.number().int().min(0),
  total: z.number().int().min(1),
  answers: z.record(z.string(), z.object({ chosen: z.number().int(), correct: z.boolean() })),
});

// ─── Sesiones de estudio ────────────────────────────────────────────────────

export const createStudySessionSchema = z.object({
  date: dateOnlySchema,
  minutes: z.number().int().min(1).max(1_440),
  note: z.string().max(500).nullable().optional(),
});

export const listStudySessionsSchema = z.object({
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
});

// ─── Repaso espaciado ───────────────────────────────────────────────────────

export const reviewItemSchema = z.object({
  id: z.string(),
  kind: reviewKindSchema,
  refId: contentIdSchema,
  lessonId: contentIdSchema,
  intervalDays: z.number().int(),
  repetitions: z.number().int(),
  dueAt: isoDateSchema,
});

export const submitReviewSchema = z.object({
  rating: reviewRatingSchema,
});

// ─── Notas ──────────────────────────────────────────────────────────────────

export const putNoteSchema = z.object({
  content: z.string().max(100_000),
});

export const noteSchema = z.object({
  lessonId: contentIdSchema,
  content: z.string(),
  updatedAt: isoDateSchema,
});

// ─── Marcadores ─────────────────────────────────────────────────────────────

export const createBookmarkSchema = z.object({
  kind: bookmarkKindSchema,
  refId: contentIdSchema,
  label: z.string().trim().min(1).max(200),
});

export const bookmarkSchema = z.object({
  id: z.string(),
  kind: bookmarkKindSchema,
  refId: contentIdSchema,
  label: z.string(),
  createdAt: isoDateSchema,
});

// ─── Snippets ───────────────────────────────────────────────────────────────

export const createSnippetSchema = z.object({
  lessonId: contentIdSchema.nullable().optional(),
  title: z.string().trim().min(1).max(120),
  language: z.string().trim().min(1).max(30).default('javascript'),
  code: z.string().max(50_000),
});

/**
 * En Zod 4, `partial()` es un MÉTODO del objeto (`schema.partial()`), no una
 * función suelta (`z.partial(schema)`), que era la API de Zod 3.
 *
 * El `.refine` de "envía al menos un campo" no es decoración: sin él, un
 * `PATCH {}` vacío se aceptaría y llegaría al repository como un UPDATE sin
 * cambios, que en algunas bases lanza error y en otras no hace nada.
 */
export const updateSnippetSchema = createSnippetSchema
  .partial()
  .refine((value) => Object.keys(value).length > 0, {
    message: 'Envía al menos un campo para actualizar',
  });

export const snippetSchema = z.object({
  id: z.string(),
  lessonId: contentIdSchema.nullable(),
  title: z.string(),
  language: z.string(),
  code: z.string(),
  createdAt: isoDateSchema,
  updatedAt: isoDateSchema,
});

// ─── Revisión semanal ───────────────────────────────────────────────────────

export const putWeeklyReviewSchema = z.object({
  did: z.string().max(10_000),
  didNotUnderstand: z.string().max(10_000),
  next: z.string().max(10_000),
  mood: z.number().int().min(1).max(5).nullable().optional(),
});

export const weeklyReviewSchema = z.object({
  weekKey: isoWeekSchema,
  did: z.string(),
  didNotUnderstand: z.string(),
  next: z.string(),
  mood: z.number().int().nullable(),
  updatedAt: isoDateSchema,
});

// ─── Gates de mes ───────────────────────────────────────────────────────────

export const setCriterionSchema = z.object({
  checked: z.boolean(),
});

export const criterionSchema = z.object({
  id: z.string(),
  month: z.number().int(),
  order: z.number().int(),
  title: z.string(),
  description: z.string(),
  required: z.boolean(),
  checked: z.boolean(),
});

export const monthGateSchema = z.object({
  month: z.number().int(),
  criteria: z.array(criterionSchema),
  requiredTotal: z.number().int(),
  requiredChecked: z.number().int(),
  /** Solo se puede abrir la evaluación del mes siguiente si esto es true. */
  unlocked: z.boolean(),
});

// ─── Portafolio ─────────────────────────────────────────────────────────────

export const updatePortfolioProjectSchema = z.object({
  status: projectStatusSchema,
  repoUrl: z.url('Debe ser una URL válida').nullable().optional(),
  liveUrl: z.url('Debe ser una URL válida').nullable().optional(),
  notes: z.string().max(5_000).nullable().optional(),
});

export const portfolioProjectSchema = z.object({
  projectKey: z.string(),
  title: z.string(),
  goal: z.string(),
  suggestedStack: z.array(z.string()),
  status: projectStatusSchema,
  repoUrl: z.url().nullable(),
  liveUrl: z.url().nullable(),
  notes: z.string().nullable(),
  updatedAt: isoDateSchema,
});

// ─── Dashboard ──────────────────────────────────────────────────────────────

/**
 * @typedef {object} MonthProgress
 * @property {number} month
 * @property {number} totalLessons
 * @property {number} completedLessons
 * @property {number} percent 0..100
 */

export const monthProgressSchema = z.object({
  month: z.number().int(),
  totalLessons: z.number().int(),
  completedLessons: z.number().int(),
  /** 0..100 */
  percent: z.number(),
});

/**
 * Un solo endpoint con todo lo que el dashboard necesita.
 *
 * Lo separé en varios (`/progress`, `/streak`, `/hours`...) y fue mala idea:
 * obligaba al frontend a hacer cinco peticiones y a coordinar cinco estados de
 * carga para pintar una pantalla. Aquí se resuelve en una respuesta.
 */
export const dashboardSchema = z.object({
  overallPercent: z.number(),
  months: z.array(monthProgressSchema),
  currentStreak: z.number().int(),
  longestStreak: z.number().int(),
  studiedToday: z.boolean(),
  minutesToday: z.number().int(),
  minutesThisWeek: z.number().int(),
  weeklyGoalMinutes: z.number().int(),
  nextLesson: z
    .object({
      id: contentIdSchema,
      title: z.string(),
      month: z.number().int(),
      week: z.number().int(),
    })
    .nullable(),
  reviewDueCount: z.number().int(),
  currentGate: monthGateSchema.nullable(),
});
